import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth
const api = axios.create({ baseURL: API });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('roomhub_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============ AUTH API ============

export const register = async (data) => {
  const response = await api.post('/auth/register', data);
  if (response.data.token) {
    localStorage.setItem('roomhub_token', response.data.token);
    localStorage.setItem('roomhub_user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const login = async (data) => {
  const response = await api.post('/auth/login', data);
  if (response.data.token) {
    localStorage.setItem('roomhub_token', response.data.token);
    localStorage.setItem('roomhub_user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const logout = () => {
  localStorage.removeItem('roomhub_token');
  localStorage.removeItem('roomhub_user');
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const updateProfile = async (data) => {
  const response = await api.put('/auth/profile', data);
  localStorage.setItem('roomhub_user', JSON.stringify(response.data));
  return response.data;
};

export const getCurrentUser = () => {
  const user = localStorage.getItem('roomhub_user');
  return user ? JSON.parse(user) : null;
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('roomhub_token');
};

// ============ LISTINGS API ============

export const getListings = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.city) params.set('city', filters.city);
    if (filters.type && filters.type !== 'all') params.set('type', filters.type);
    if (filters.types) params.set('types', filters.types);  // Multiple types from homepage
    if (filters.maxPrice && filters.maxPrice !== '0') params.set('maxPrice', filters.maxPrice);
    if (filters.minSize && filters.minSize !== '0') params.set('minSize', filters.minSize);
    if (filters.furnished) params.set('furnished', 'true');
    if (filters.radius) params.set('radius', filters.radius);
    
    const response = await api.get(`/listings?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching listings:', error);
    throw error;
  }
};

export const getListing = async (id) => {
  const response = await api.get(`/listings/${id}`);
  return response.data;
};

export const createListing = async (listingData) => {
  const response = await api.post('/listings', listingData);
  return response.data;
};

export const updateListing = async (id, listingData) => {
  const response = await api.put(`/listings/${id}`, listingData);
  return response.data;
};

export const getListingForEdit = async (id) => {
  const response = await api.get(`/listings/${id}/edit`);
  return response.data;
};

export const deleteListing = async (id) => {
  const response = await api.delete(`/listings/${id}`);
  return response.data;
};

export const getMyListings = async () => {
  const response = await api.get('/my-listings');
  return response.data;
};

// ============ ADMIN API ============

export const getAdminStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

export const getAdminPendingListings = async () => {
  const response = await api.get('/admin/pending-listings');
  return response.data;
};

export const getAdminAllListings = async (status = null) => {
  const params = status ? `?status=${status}` : '';
  const response = await api.get(`/admin/all-listings${params}`);
  return response.data;
};

export const getAdminAllUsers = async () => {
  const response = await api.get('/admin/all-users');
  return response.data;
};

export const approveListing = async (id) => {
  const response = await api.post(`/admin/listings/${id}/approve`);
  return response.data;
};

export const rejectListing = async (id, reason) => {
  const response = await api.post(`/admin/listings/${id}/reject`, { reason });
  return response.data;
};

export const adminDeleteListing = async (id) => {
  const response = await api.delete(`/admin/listings/${id}`);
  return response.data;
};

// ============ FAVORITES API ============

export const addFavorite = async (listingId) => {
  const response = await api.post(`/favorites/${listingId}`);
  return response.data;
};

export const removeFavorite = async (listingId) => {
  const response = await api.delete(`/favorites/${listingId}`);
  return response.data;
};

export const getFavorites = async () => {
  const response = await api.get('/favorites');
  return response.data;
};

// ============ REACTIONS API ============

export const createReaction = async (data) => {
  const response = await api.post('/reactions', data);
  return response.data;
};

export const getListingReactions = async (listingId) => {
  const response = await api.get(`/reactions/listing/${listingId}`);
  return response.data;
};

export const getMyReactions = async () => {
  const response = await api.get('/reactions/my');
  return response.data;
};

// ============ MESSAGES API ============

export const getConversations = async () => {
  const response = await api.get('/messages/conversations');
  return response.data;
};

export const getMessages = async (listingId, otherUserId) => {
  const response = await api.get(`/messages/${listingId}/${otherUserId}`);
  return response.data.messages || [];
};

export const sendMessage = async (data) => {
  const response = await api.post('/messages', data);
  return response.data;
};

// ============ CITIES API ============

export const getCities = async () => {
  const response = await api.get('/cities');
  return response.data;
};

// ============ SWIPEMATCH API ============

export const saveSwipePreferences = async (data) => {
  const response = await api.post('/swipematch/preferences', data);
  return response.data;
};

export const getSwipePreferences = async () => {
  const response = await api.get('/swipematch/preferences');
  return response.data;
};

export const getSwipeListings = async () => {
  const response = await api.get('/swipematch/listings');
  return response.data;
};

export const swipeListing = async (listingId, direction) => {
  const response = await api.post('/swipematch/swipe', { listingId, direction });
  return response.data;
};

export const getSwipeMatches = async () => {
  const response = await api.get('/swipematch/matches');
  return response.data;
};

// Alias for dashboard
export const getMatches = getSwipeMatches;

export const getSwipeStats = async () => {
  const response = await api.get('/swipematch/stats');
  return response.data;
};

export const resetSwipeHistory = async () => {
  const response = await api.post('/swipematch/reset-history');
  return response.data;
};

// ============ DASHBOARD CHAT API ============

export const getDashboardChat = async (listingId, otherUserId) => {
  const response = await api.get(`/dashboard/chat/${listingId}/${otherUserId}`);
  return response.data;
};

export const sendDashboardChat = async (matchId, listingId, receiverId, content) => {
  const response = await api.post('/dashboard/chat', {
    matchId,
    listingId,
    receiverId,
    content
  });
  return response.data;
};

// ============ SUBSCRIPTION API ============

export const startFreeTrial = async () => {
  const response = await api.post('/subscription/start-trial');
  return response.data;
};

// ============ PAYMENT API ============

export const createCheckoutSession = async (packageId = 'swipematch_monthly', returnPath = '/premium') => {
  const originUrl = window.location.origin;
  const response = await api.post('/payments/checkout', {
    package_id: packageId,
    origin_url: originUrl,
    return_path: returnPath
  });
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await api.get(`/payments/status/${sessionId}`);
  return response.data;
};

export default api;
